/**
 * @module components/TierTour
 * @description Demo-only "What this tier adds" panel (Enhanced and Immersive) in the demo bar, so each
 * Q1314 Tier 2 line item can be SHOWN on the call in one click instead of
 * being hunted for. Not part of the production page.
 *
 * Every action triggers the real feature (never a mock-up of it):
 *   - Replay loading   → reloads the causes in place (skeletons, then staggered cards)
 *   - Match me         → opens the real guide at question 1, in view and focused
 *   - Guided checkout  → puts two featured causes in the cart and opens checkout
 *   - Saved causes     → explains the persistence (it is invisible by nature)
 */
import { useState, type JSX } from "react";
import { useGiving } from "../app/GivingContext";
import { navigate } from "../app/router";
import { DEFAULT_GIFT_USD } from "../config/page";
import { CURRENT_TIER } from "../config/tier";
import type { Cause } from "../domain/types";
import { OPEN_MATCH_EVENT } from "./MatchMe";
import { Icon } from "./Icons";
import "./TierTour.css";

/** One tour row. No `action` = informational (the feature is used in place). */
interface TourItem {
    title: string;
    description: string;
    actionLabel: string;
    action?: () => void;
}

/** Window event the Immersive tour fires to play a sample Shot (handled by StageProvider). */
export const TOUR_SHOT_EVENT: string = "afj:tour-shot";

/** The tour: Enhanced and Immersive (Standard IS the brief, so it has nothing to add). */
export function TierTour(): JSX.Element | null {
    const { causes, dispatchCart, replayLoading, setCartOpen } = useGiving();
    const [open, setOpen] = useState<boolean>(false);
    if (CURRENT_TIER === "standard") return null;
    const firstFeatured: Cause | undefined = (causes ?? []).find((c: Cause) => c.featured);

    /** Seeds the cart with two featured causes, then opens the guided checkout. */
    const tryCheckout = (): void => {
        (causes ?? []).filter((c: Cause) => c.featured).slice(0, 2)
            .forEach((c: Cause) => dispatchCart({ type: "add", causeId: c.id, amountUsd: DEFAULT_GIFT_USD }));
        setOpen(false);
        navigate("checkout");
    };
    const tryMatch = (): void => {
        setOpen(false);
        navigate("browse");
        // Let the browse route mount the guide before asking it to open.
        window.setTimeout(() => window.dispatchEvent(new Event(OPEN_MATCH_EVENT)), 150);
    };
    /** In-page replay: skeletons in view, held a visible beat, then cards stagger in. */
    const replay = (): void => {
        setOpen(false);
        navigate("browse");
        replayLoading();
        window.setTimeout(() => document.querySelector(".skeleton-browse")?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
    };

    /** Immersive actions. */
    const openStage = (): void => {
        setOpen(false);
        if (firstFeatured) navigate("stage", firstFeatured.id);
    };
    const playShot = (): void => {
        setOpen(false);
        if (!firstFeatured) return;
        navigate("stage", firstFeatured.id);
        window.setTimeout(() => window.dispatchEvent(new CustomEvent(TOUR_SHOT_EVENT, { detail: firstFeatured.id })), 300);
    };
    const showLive = (): void => {
        setOpen(false);
        navigate("browse");
        window.setTimeout(() => document.getElementById("live-now")?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
    };
    const openQuiver = (): void => {
        setOpen(false);
        setCartOpen(true);
    };

    /** Each tier's Q1314 items, as data (one row template renders them all). */
    const immersiveItems: TourItem[] = [
        { title: "Stages + Legend Arrows", description: "Every cause is a stage; gifts are US$ arrows named for Jamaica's Legends.", actionLabel: "Show me", action: openStage },
        { title: "Cupid celebration", description: "Each Shot plays Cupid on your screen; Twin arrows play doubled.", actionLabel: "Play one", action: playShot },
        { title: "Live Now + love-stream", description: "Stages ranked by live activity, with a streaming feed (simulated here).", actionLabel: "Show me", action: showLive },
        { title: "The Quiver", description: "Arrows for many stages, one US$ total, one checkout through Neon.", actionLabel: "Open it", action: openQuiver },
    ];
    const enhancedItems: TourItem[] = [
        { title: "Guided checkout", description: "One question per screen, with progress. Resumes after a reload.", actionLabel: "Show me", action: tryCheckout },
        { title: "Match me to a cause", description: "Three quick questions; every match shows why it fits.", actionLabel: "Show me", action: tryMatch },
        { title: "Motion system", description: "Skeleton loading, page transitions, staggered cards. Off under reduced motion.", actionLabel: "Replay", action: replay },
        { title: "Saved causes remembered", description: "Tap the heart on any card; it stays saved on this device after you leave.", actionLabel: "On every card" },
    ];
    const items: TourItem[] = CURRENT_TIER === "immersive" ? immersiveItems : enhancedItems;
    const tierName: string = CURRENT_TIER === "immersive" ? "Immersive" : "Enhanced";
    const baseline: string = CURRENT_TIER === "immersive" ? "Everything in Enhanced" : "Everything in Standard";

    return (
        <div className="tier-tour">
            <button type="button" className="tier-tour__toggle" aria-expanded={open} onClick={() => setOpen((o: boolean) => !o)}>
                <Icon name="sparkle" size={14} /> What {tierName} adds ({items.length})
            </button>
            {open && (
                <div className="tier-tour__panel" role="dialog" aria-label={`What ${tierName} adds`}>
                    <header className="tier-tour__head">
                        <p className="tier-tour__title">What {tierName} adds</p>
                        <p className="tier-tour__sub">{baseline}, plus these {items.length}. Each one runs live.</p>
                    </header>
                    <ol className="tier-tour__list">
                        {items.map((item: TourItem, i: number) => (
                            <li key={item.title} className="tier-tour__item">
                                <span className="tier-tour__num" aria-hidden="true">{i + 1}</span>
                                <span className="tier-tour__text">
                                    <strong className="tier-tour__name">{item.title}</strong>
                                    <span className="tier-tour__desc">{item.description}</span>
                                </span>
                                {item.action !== undefined ? (
                                    <button type="button" className="btn btn--sm btn--ghost tier-tour__action" onClick={item.action}>
                                        {item.actionLabel} <Icon name="arrow-right" size={14} />
                                    </button>
                                ) : (
                                    <span className="tier-tour__note">{item.actionLabel}</span>
                                )}
                            </li>
                        ))}
                    </ol>
                </div>
            )}
        </div>
    );
}
